import React, { useEffect, useRef, useState } from "react";
import { toast } from "../../../utils/notify";
import ApiLoader from "../../ui/ApiLoader";
import { DataTable } from "../../common/DataTable";
import { Eye, Trash2, X } from "lucide-react";
import {
  fetchFindings,
  fetchFindingById,
  createFinding,
  deleteFinding,
} from "../../../services/axios/AuthApis/DashboardApis";

const SEVERITY_OPTIONS = ["critical", "high", "medium", "low"];
const STATUS_OPTIONS = ["open", "in_progress", "resolved", "suppressed"];
const CLOUD_PROVIDER_OPTIONS = ["aws", "azure", "gcp"];
const DATA_TYPE_OPTIONS = ["pii", "phi", "pci", "credentials", "financial"];

const Findings = ({ activeTab }) => {
  const [pageSize, setPageSize] = useState(10);

  const [findings, setFindings] = useState([]);

  const [findingsLoading, setFindingsLoading] = useState(false);

  const [findingsSearch, setFindingsSearch] = useState("");

  const [currentPage, setCurrentPage] = useState(1);

  const [totalPages, setTotalPages] = useState(1);

  const [totalCount, setTotalCount] = useState(0);

  const [hasNext, setHasNext] = useState(false);

  const [hasPrevious, setHasPrevious] = useState(false);

  const [selectedFinding, setSelectedFinding] = useState(null);

  const [showViewModal, setShowViewModal] = useState(false);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [findingToDelete, setFindingToDelete] = useState(null);

  const [deletingFinding, setDeletingFinding] = useState(false);

  const [showAddModal, setShowAddModal] = useState(false);

  const [savingFinding, setSavingFinding] = useState(false);

  const [newFinding, setNewFinding] = useState({
    title: "",
    description: "",
    severity: "",
    status: "open",
    cloud_provider: "",
    data_type: "",
    affected_records: 0,
    remediation_steps: "",
  });

  // severity/status/cloud_provider/data_type are real backend query
  // params on list_findings (already implemented server-side, just
  // never wired to the UI) - finding_id/asset_name/rule_name/title all
  // fold into the existing `search` param, which already matches
  // title/store_name there.
  const [findingColumnFilters, setFindingColumnFilters] = useState({
    severity: "", status: "", cloud_provider: "", data_type: "",
  });
  const findingsRequestIdRef = useRef(0);

  const loadFindings = async (page = 1, size = pageSize, showLoader = true, colFilters = findingColumnFilters) => {
    const requestId = ++findingsRequestIdRef.current;
    try {
      if (showLoader) {
        setFindingsLoading(true);
      }

      const res = await fetchFindings({
        page,
        page_size: size,
        search: findingsSearch || undefined,
        severity: colFilters.severity || undefined,
        status: colFilters.status || undefined,
        cloud_provider: colFilters.cloud_provider || undefined,
        data_type: colFilters.data_type || undefined,
      });

      // A newer loadFindings() call started since this one went out -
      // its response is stale even if it resolves last.
      if (requestId !== findingsRequestIdRef.current) return;

      setFindings(res.results || []);
      setCurrentPage(res.current_page || page);
      setTotalPages(res.total_pages || 1);
      setTotalCount(res.total_count || 0);
      setHasNext(!!res.has_next);
      setHasPrevious(!!res.has_previous);
      setPageSize(size);
    } catch (err) {
      if (requestId === findingsRequestIdRef.current) toast.error("Unable to load findings");
    } finally {
      if (showLoader && requestId === findingsRequestIdRef.current) {
        setFindingsLoading(false);
      }
    }
  };

  const handlePageChange = (page, size) => {
    loadFindings(page, size);
  };

  const handleFindingsSearch = (e) => {
    setFindingsSearch(e.target.value);
  };

  const handleNewFindingChange = (e) => {
    const { name, value } = e.target;

    setNewFinding((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddFinding = async () => {
    if (!newFinding.title.trim()) {
      toast.error("Title is required");
      return;
    }

    try {
      setSavingFinding(true);

      await createFinding(newFinding);

      toast.success("Finding Added Successfully");

      setShowAddModal(false);

      setNewFinding({
        title: "",
        description: "",
        severity: "",
        status: "open",
        cloud_provider: "",
        data_type: "",
        affected_records: 0,
        remediation_steps: "",
      });

      await loadFindings(1, pageSize, false);
    } catch (err) {
      console.log(err);
      toast.error("Failed to Add Finding");
    } finally {
      setSavingFinding(false);
    }
  };

  const handleViewFinding = async (findingId) => {
    try {
      const response = await fetchFindingById(findingId);

      setSelectedFinding(response.data || response);

      setShowViewModal(true);
    } catch (err) {
      console.log(err);
      toast.error("Unable to load finding details");
    }
  };

  const handleAskDeleteFinding = (finding) => {
    setFindingToDelete(finding);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDeleteFinding = async () => {
    if (!findingToDelete) return;

    try {
      setDeletingFinding(true);

      await deleteFinding(findingToDelete.finding_id);

      toast.success("Finding deleted successfully");

      setShowDeleteConfirm(false);
      setFindingToDelete(null);

      await loadFindings(currentPage, pageSize, false);
    } catch (err) {
      console.log(err);
      toast.error("Failed to delete finding");
    } finally {
      setDeletingFinding(false);
    }
  };

  // Re-run from page 1 whenever search or a real backend filter changes.
  useEffect(() => {
    if (activeTab === "findings") {
      loadFindings(1, pageSize, true, findingColumnFilters);
    }
  }, [activeTab, findingsSearch, findingColumnFilters]);

  // Finding ID/Asset Name/Rule Name have no dedicated backend filter
  // param, so they're filtered client-side on whatever page is
  // currently loaded (same as Title in Document Library).
  const [localFilters, setLocalFilters] = useState({ finding_id: "", asset_name: "", rule_name: "" });

  const displayedFindings = findings.filter((f) => {
    if (localFilters.finding_id && !String(f.finding_id || "").toLowerCase().includes(localFilters.finding_id.toLowerCase())) return false;
    if (localFilters.asset_name && !String(f.asset_name || "").toLowerCase().includes(localFilters.asset_name.toLowerCase())) return false;
    if (localFilters.rule_name && !String(f.rule_name || "").toLowerCase().includes(localFilters.rule_name.toLowerCase())) return false;
    return true;
  });

  const columns = [
    {
      id: "finding_id",
      accessorFn: (f) => f.finding_id,
      header: "Finding ID",
      enableSorting: false,
      meta: { filter: { variant: "text", label: "Finding ID", placeholder: "Search finding ID" } },
      cell: ({ row }) => <span>{row.original.finding_id}</span>,
    },
    {
      id: "title",
      accessorFn: (f) => f.title,
      header: "Title",
      enableSorting: false,
      meta: { filter: { variant: "text", label: "Title", placeholder: "Search title" } },
      cell: ({ row }) => <span>{row.original.title}</span>,
    },
    {
      id: "asset_name",
      accessorFn: (f) => f.asset_name,
      header: "Asset Name",
      enableSorting: false,
      meta: { filter: { variant: "text", label: "Asset Name", placeholder: "Search asset name" } },
      cell: ({ row }) => <span>{row.original.asset_name}</span>,
    },
    {
      id: "rule_name",
      accessorFn: (f) => f.rule_name,
      header: "Rule Name",
      enableSorting: false,
      meta: { filter: { variant: "text", label: "Rule Name", placeholder: "Search rule name" } },
      cell: ({ row }) => <span>{row.original.rule_name}</span>,
    },
    {
      id: "severity",
      accessorFn: (f) => f.severity,
      header: "Severity",
      enableSorting: false,
      meta: { filter: { variant: "select", label: "Severity", placeholder: "All severities", options: SEVERITY_OPTIONS.map((v) => ({ value: v, label: v.charAt(0).toUpperCase() + v.slice(1) })) } },
      cell: ({ row }) => <span className="capitalize">{row.original.severity}</span>,
    },
    {
      id: "status",
      accessorFn: (f) => f.status,
      header: "Status",
      enableSorting: false,
      meta: { filter: { variant: "select", label: "Status", placeholder: "All statuses", options: STATUS_OPTIONS.map((v) => ({ value: v, label: v.replace("_", " ") })) } },
      cell: ({ row }) => {
        const status = row.original.status;
        return (
          <span
            className={`px-3 py-1 rounded-full text-sm capitalize ${
              status === "resolved" ? "bg-green-100 text-green-700"
                : status === "in_progress" ? "bg-yellow-100 text-yellow-700"
                : status === "suppressed" ? "bg-gray-200 text-gray-700"
                : "bg-red-100 text-red-700"
            }`}
          >
            {(status || "").replace("_", " ")}
          </span>
        );
      },
    },
    {
      id: "cloud_provider",
      accessorFn: (f) => f.cloud_provider,
      header: "Cloud Provider",
      enableSorting: false,
      meta: { filter: { variant: "select", label: "Cloud Provider", placeholder: "All providers", options: CLOUD_PROVIDER_OPTIONS.map((v) => ({ value: v, label: v.toUpperCase() })) } },
      cell: ({ row }) => <span>{row.original.cloud_provider}</span>,
    },
    {
      id: "data_type",
      accessorFn: (f) => f.data_type,
      header: "Data Type",
      enableSorting: false,
      meta: { filter: { variant: "select", label: "Data Type", placeholder: "All data types", options: DATA_TYPE_OPTIONS.map((v) => ({ value: v, label: v.toUpperCase() })) } },
      cell: ({ row }) => <span>{row.original.data_type}</span>,
    },
    {
      id: "detected_at",
      accessorFn: (f) => f.detected_at,
      header: "Detected At",
      enableSorting: false,
      cell: ({ row }) => <span>{row.original.detected_at}</span>,
    },
    {
      id: "actions",
      header: "Actions",
      enableSorting: false,
      cell: ({ row }) => {
        const finding = row.original;
        return (
          <div className="flex gap-2">
            <button onClick={() => handleViewFinding(finding.finding_id)} className="border p-2 rounded">
              <Eye size={18} />
            </button>
            <button onClick={() => handleAskDeleteFinding(finding)} className="border p-2 rounded text-red-600">
              <Trash2 size={18} />
            </button>
          </div>
        );
      },
    },
  ];

  const columnFilters = [
    ...(findingsSearch ? [{ id: "title", value: findingsSearch }] : []),
    ...Object.entries(localFilters).filter(([, v]) => v).map(([id, value]) => ({ id, value })),
    ...Object.entries(findingColumnFilters).filter(([, v]) => v).map(([id, value]) => ({ id, value })),
  ];

  // Full-page loader only on the very first load (no rows yet) - a
  // filter-triggered refetch must not unmount the whole tab, only dim
  // the table (DataTable's own loading prop already handles that).
  if (findingsLoading && findings.length === 0) {
    return (
      <ApiLoader variant="spinner" label="Loading Findings..." className="py-20" />
    );
  }

  return (
    <>
      {activeTab === "findings" && (
        <>
          <h2 className="text-2xl font-bold mb-6">Findings</h2>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl shadow p-5">
              <p className="text-gray-500 text-sm">Total Findings</p>

              <h2 className="text-3xl font-bold mt-2">{totalCount}</h2>
            </div>

            <div className="bg-white rounded-xl shadow p-5">
              <p className="text-gray-500 text-sm">Open Findings</p>

              <h2 className="text-3xl font-bold mt-2 text-red-600">
                {findings.filter((f) => f.status === "open").length}
              </h2>
            </div>

            <div className="bg-white rounded-xl shadow p-5">
              <p className="text-gray-500 text-sm">Resolved Findings</p>

              <h2 className="text-3xl font-bold mt-2 text-green-600">
                {findings.filter((f) => f.status === "resolved").length}
              </h2>
            </div>

            <div className="bg-white rounded-xl shadow p-5">
              <p className="text-gray-500 text-sm">Critical Findings</p>

              <h2 className="text-3xl font-bold mt-2 text-orange-600">
                {
                  findings.filter(
                    (f) => f.severity === "critical" || f.severity === "high",
                  ).length
                }
              </h2>
            </div>
          </div>

          {/* Findings Table */}
          <div className="bg-white rounded-xl shadow mt-6 p-6">
            <div className="flex flex-wrap justify-between items-center gap-3 mb-5">
              <h2 className="text-xl font-bold">Findings List</h2>
            </div>

            <DataTable
              columns={columns}
              data={displayedFindings}
              getRowId={(f) => String(f.finding_id)}
              loading={findingsLoading}
              emptyMessage="No Findings Found"
              manualPagination
              rowCount={totalCount}
              pagination={{ pageIndex: (currentPage || 1) - 1, pageSize }}
              onPaginationChange={(updater) => {
                const currentPagination = { pageIndex: (currentPage || 1) - 1, pageSize };
                const nextPagination = typeof updater === 'function' ? updater(currentPagination) : updater;
                loadFindings(nextPagination.pageIndex + 1, nextPagination.pageSize, true, findingColumnFilters);
              }}
              enableColumnVisibility={false}
              enableColumnResizing
              enableColumnFilters
              manualFiltering
              columnFilters={columnFilters}
              onColumnFiltersChange={(updater) => {
                const nextFilters = typeof updater === 'function' ? updater(columnFilters) : updater;
                const values = Object.fromEntries(nextFilters.map(({ id, value }) => [id, value]));
                setFindingsSearch(values.title || "");
                setLocalFilters({
                  finding_id: values.finding_id || "",
                  asset_name: values.asset_name || "",
                  rule_name: values.rule_name || "",
                });
                setFindingColumnFilters({
                  severity: values.severity || "",
                  status: values.status || "",
                  cloud_provider: values.cloud_provider || "",
                  data_type: values.data_type || "",
                });
              }}
              maxBodyHeight="65vh"
            />
          </div>

          {/* Add Finding Modal */}
          {showAddModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl relative overflow-hidden max-h-[90vh] flex flex-col">
                {savingFinding && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white/70 rounded-2xl z-50">
                    <ApiLoader variant="spinner" label="Saving Finding..." />
                  </div>
                )}

                <div className="bg-gradient-to-r from-brand to-brand-dark px-6 py-5 flex items-start justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-white">
                      Add Finding
                    </h2>
                    <p className="text-sm text-blue-100">
                      Fill in the finding details below
                    </p>
                  </div>

                  <button
                    onClick={() => setShowAddModal(false)}
                    className="text-white/80 hover:text-white hover:bg-white/10 rounded-lg p-1.5 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="p-6 overflow-y-auto space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">
                      Title*
                    </label>
                    <input
                      type="text"
                      name="title"
                      placeholder="Title*"
                      value={newFinding.title}
                      onChange={handleNewFindingChange}
                      className="w-full border-0 rounded-xl bg-gray-50 p-3 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">
                      Description
                    </label>
                    <textarea
                      name="description"
                      placeholder="Description"
                      value={newFinding.description}
                      onChange={handleNewFindingChange}
                      rows={3}
                      className="w-full border-0 rounded-xl bg-gray-50 p-3 focus:ring-2 focus:ring-blue-500 focus:outline-none resize-none"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">
                        Severity
                      </label>
                      <select
                        name="severity"
                        value={newFinding.severity}
                        onChange={handleNewFindingChange}
                        className="w-full border-0 rounded-xl bg-gray-50 p-3 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      >
                        <option value="">Select Severity</option>
                        {SEVERITY_OPTIONS.map((option) => (
                          <option key={option} value={option}>
                            {option.charAt(0).toUpperCase() + option.slice(1)}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">
                        Status
                      </label>
                      <select
                        name="status"
                        value={newFinding.status}
                        onChange={handleNewFindingChange}
                        className="w-full border-0 rounded-xl bg-gray-50 p-3 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      >
                        {STATUS_OPTIONS.map((option) => (
                          <option key={option} value={option}>
                            {option.replace("_", " ")}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">
                        Cloud Provider
                      </label>
                      <select
                        name="cloud_provider"
                        value={newFinding.cloud_provider}
                        onChange={handleNewFindingChange}
                        className="w-full border-0 rounded-xl bg-gray-50 p-3 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      >
                        <option value="">Select Cloud Provider</option>
                        {CLOUD_PROVIDER_OPTIONS.map((option) => (
                          <option key={option} value={option}>
                            {option.toUpperCase()}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">
                        Data Type
                      </label>
                      <select
                        name="data_type"
                        value={newFinding.data_type}
                        onChange={handleNewFindingChange}
                        className="w-full border-0 rounded-xl bg-gray-50 p-3 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      >
                        <option value="">Select Data Type</option>
                        {DATA_TYPE_OPTIONS.map((option) => (
                          <option key={option} value={option}>
                            {option.toUpperCase()}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">
                      Affected Records
                    </label>
                    <input
                      type="number"
                      name="affected_records"
                      min={0}
                      value={newFinding.affected_records}
                      onChange={handleNewFindingChange}
                      className="w-full border-0 rounded-xl bg-gray-50 p-3 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">
                      Remediation Steps
                    </label>
                    <textarea
                      name="remediation_steps"
                      placeholder="Remediation Steps"
                      value={newFinding.remediation_steps}
                      onChange={handleNewFindingChange}
                      rows={4}
                      className="w-full border-0 rounded-xl bg-gray-50 p-3 focus:ring-2 focus:ring-blue-500 focus:outline-none resize-none"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100">
                  <button
                    onClick={() => setShowAddModal(false)}
                    className="border border-gray-300 text-gray-700 px-5 py-2.5 rounded-xl font-medium hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>

                  <button
                    onClick={handleAddFinding}
                    disabled={savingFinding || !newFinding.title.trim()}
                    className={`px-5 py-2.5 rounded-xl font-medium text-white transition-colors ${
                      savingFinding || !newFinding.title.trim()
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-blue-600 hover:bg-blue-700"
                    }`}
                  >
                    {savingFinding ? (
                      <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      "Save Finding"
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* View Finding Modal */}
          {showViewModal && selectedFinding && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-white rounded-xl w-[600px] max-h-[90vh] overflow-y-auto p-6">
                <div className="flex justify-between items-center mb-5">
                  <h2 className="text-xl font-bold">Finding Details</h2>

                  <button
                    onClick={() => {
                      setShowViewModal(false);
                      setSelectedFinding(null);
                    }}
                    className="text-xl"
                  >
                    ✕
                  </button>
                </div>

                <div className="space-y-6">
                  <div>
                    <h3 className="text-sm font-bold uppercase text-black mb-2">
                      Description
                    </h3>

                    <div className="border rounded-xl p-4 text-white bg-blue-600">
                      {selectedFinding.description || "No description"}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-x-10 gap-y-5">
                    <div>
                      <p className="text-xs text-gray-500 uppercase">
                        Finding ID
                      </p>
                      <p className="font-medium">
                        {selectedFinding.finding_id}
                      </p>
                    </div>

                    <div>
                      <p className="text-xs text-gray-500 uppercase">Title</p>
                      <p className="font-medium">{selectedFinding.title}</p>
                    </div>

                    <div>
                      <p className="text-xs text-gray-500 uppercase">
                        Asset Name
                      </p>
                      <p>{selectedFinding.asset_name}</p>
                    </div>

                    <div>
                      <p className="text-xs text-gray-500 uppercase">
                        Rule Name
                      </p>
                      <p>{selectedFinding.rule_name}</p>
                    </div>

                    <div>
                      <p className="text-xs text-gray-500 uppercase">
                        Severity
                      </p>
                      <span className="px-3 py-1 rounded-full bg-red-100 text-red-600 capitalize">
                        {selectedFinding.severity}
                      </span>
                    </div>

                    <div>
                      <p className="text-xs text-gray-500 uppercase">
                        Status
                      </p>
                      <span
                        className={`px-3 py-1 rounded-full capitalize ${
                          selectedFinding.status === "resolved"
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700"
                        }`}
                      >
                        {(selectedFinding.status || "").replace("_", " ")}
                      </span>
                    </div>

                    <div>
                      <p className="text-xs text-gray-500 uppercase">
                        Cloud Provider
                      </p>
                      <p>{selectedFinding.cloud_provider}</p>
                    </div>

                    <div>
                      <p className="text-xs text-gray-500 uppercase">
                        Data Type
                      </p>
                      <p>{selectedFinding.data_type}</p>
                    </div>

                    <div>
                      <p className="text-xs text-gray-500 uppercase">
                        Affected Records
                      </p>
                      <p>{selectedFinding.affected_records ?? "-"}</p>
                    </div>

                    <div>
                      <p className="text-xs text-gray-500 uppercase">
                        Detected At
                      </p>
                      <p>{selectedFinding.detected_at}</p>
                    </div>

                    <div>
                      <p className="text-xs text-gray-500 uppercase">
                        Updated At
                      </p>
                      <p>{selectedFinding.updated_at}</p>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-semibold uppercase text-gray-500 mb-2">
                      Remediation
                    </h3>

                    <div className="border rounded-xl p-4 bg-gray-50">
                      {selectedFinding.remediation_steps || "-"}
                    </div>
                  </div>
                </div>

                <div className="flex justify-end mt-6">
                  <button
                    onClick={() => {
                      setShowViewModal(false);
                      setSelectedFinding(null);
                    }}
                    className="bg-blue-600 text-white px-5 py-2 rounded-lg"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Delete Confirmation Modal */}
          {showDeleteConfirm && findingToDelete && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-white rounded-xl w-[420px] p-6">
                <h2 className="text-lg font-bold mb-2">Delete Finding</h2>

                <p className="text-gray-600 text-sm mb-6">
                  Are you sure you want to delete finding{" "}
                  <span className="font-semibold">
                    {findingToDelete.finding_id}
                  </span>
                  {" "}({findingToDelete.title})? This action cannot be
                  undone.
                </p>

                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setFindingToDelete(null);
                    }}
                    disabled={deletingFinding}
                    className="border border-gray-300 text-gray-700 px-4 py-2 rounded-lg"
                  >
                    Cancel
                  </button>

                  <button
                    onClick={handleConfirmDeleteFinding}
                    disabled={deletingFinding}
                    className="bg-red-600 text-white px-4 py-2 rounded-lg disabled:opacity-60"
                  >
                    {deletingFinding ? "Deleting..." : "Delete"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default Findings;
